import { formatOverAmount } from '~support-at-home/lib/budgetFormat';
import {
  addOverLimits,
  amountBeyond,
  mostSeriousTone,
  OverTone,
  OverTotals,
  splitOverLimit,
  toCents,
  withinLimit,
} from '~support-at-home/lib/overLimits';

import { DashboardBudget } from '~support-at-home/models/dashboard';

import { DASHBOARD_TEXT } from './dashboardText';

// The Dashboard's lists, ranking, totals and banner (TAV-3463): pure, built from the API's one-row-per-budget list with
// the same over-limit rules (claimed first) the Budget Tracker uses, so the two pages count every limit the same way.
// The API's dashboardBudgetLists (sah_budget_figures/rules.mjs) repeats the list rules for the Show all export, pinned
// to this file by a parity test: change both together.

export type BreakdownKey = 'allocation' | 'utilisation' | 'care-management';
export type ListSide = 'over' | 'under';

/**
 * One budget in a list. `booked` and `claimed` are the care management figures in the care management lists, the
 * budget's own otherwise; `allowance` is always the care management allowance.
 */
export interface BudgetListRow {
  id: string;
  budget_id: string;
  reference: string;
  client_name: string;
  /** The client's case manager, or "Unassigned" (TAV-3482) */
  case_manager: string;
  start_date: string;
  funding_type: string;
  available: number;
  allocated: number;
  booked: number;
  claimed: number;
  allowance: number;
  /** Care management appointments counted on the budget, cancelled left out (TAV-3482) */
  cm_appointments: number;
  /** The booked and claimed parts of the amount over the limit, claimed counting first; 0 in the Under lists. */
  booked_over: number;
  claimed_over: number;
  /** What the list ranks by: over by, Residual, Left to book or Unused (dollars, to the cent) */
  amount: number;
  /** Allocation rate, Utilisation or care management Usage (ratio); null when there is no divisor */
  rate: number | null;
}

export interface BudgetList {
  /** Ranked: biggest amount first */
  rows: BudgetListRow[];
  /** The amounts added up (dollars) */
  total: number;
  /** The over amounts added up with how many budgets are over in each part; null where the tone is not booked or claimed */
  over: OverTotals | null;
}

export type DashboardBreakdowns = Record<BreakdownKey, Record<ListSide, BudgetList>>;

const compareText = (a: string, b: string): number => a.localeCompare(b, 'en-AU', { sensitivity: 'base' });

/** Biggest amount (to the cent) first, then client name A to Z, start date, reference and budget id. */
const rankByAmount = (rows: BudgetListRow[]): BudgetListRow[] =>
  [...rows].sort(
    (a, b) =>
      toCents(b.amount) - toCents(a.amount) ||
      compareText(a.client_name, b.client_name) ||
      compareText(a.start_date, b.start_date) ||
      compareText(a.reference, b.reference) ||
      compareText(a.budget_id, b.budget_id),
  );

const rateOf = (numerator: number, divisor: number): number | null => (divisor > 0 ? numerator / divisor : null);

const toRow = (
  budget: DashboardBudget,
  figures: Pick<BudgetListRow, 'booked' | 'claimed' | 'amount' | 'rate'> &
    Partial<Pick<BudgetListRow, 'booked_over' | 'claimed_over'>>,
): BudgetListRow => ({
  id: budget.budget_id,
  budget_id: budget.budget_id,
  reference: budget.reference,
  client_name: budget.client_name,
  case_manager: budget.case_manager_name ?? DASHBOARD_TEXT.columns.caseManager.unassigned,
  start_date: budget.start_date,
  funding_type: budget.funding_type ?? '',
  available: budget.available,
  allocated: budget.allocated,
  allowance: budget.cm_allowance,
  cm_appointments: budget.cm_appointments,
  booked_over: 0,
  claimed_over: 0,
  ...figures,
});

const toList = (rows: BudgetListRow[], over: OverTotals | null = null): BudgetList => ({
  rows: rankByAmount(rows),
  total: rows.reduce((sum, row) => sum + toCents(row.amount), 0) / 100,
  over,
});

const overRow = (
  budget: DashboardBudget,
  limit: number,
  figures: { booked: number; claimed: number },
  rate: number | null,
): { row: BudgetListRow; booked: number; claimed: number; total: number } | null => {
  const over = splitOverLimit(limit, figures);
  if (over.total <= 0) return null;
  return {
    ...over,
    row: toRow(budget, { ...figures, amount: over.total, rate, booked_over: over.booked, claimed_over: over.claimed }),
  };
};

/**
 * The six lists: Over and Under for Allocation, Utilisation and Care management, each budget on its own (never netted
 * against another), ranked by dollars with no cap. A budget exactly at its limit is in neither list.
 */
export const buildBreakdowns = (budgets: DashboardBudget[]): DashboardBreakdowns => {
  const allocationOver: BudgetListRow[] = [];
  const allocationUnder: BudgetListRow[] = [];
  const utilisationOver: BudgetListRow[] = [];
  const utilisationUnder: BudgetListRow[] = [];
  const careOver: BudgetListRow[] = [];
  const careUnder: BudgetListRow[] = [];

  budgets.forEach((budget) => {
    const used = { booked: budget.booked, claimed: budget.claimed };
    const allocationRate = rateOf(budget.allocated, budget.available);
    const utilisationRate = rateOf(budget.booked + budget.claimed, budget.allocated);

    const beyondFunding = amountBeyond(budget.allocated, budget.available);
    if (beyondFunding > 0) allocationOver.push(toRow(budget, { ...used, amount: beyondFunding, rate: allocationRate }));
    const residual = amountBeyond(budget.available, budget.allocated);
    if (residual > 0) allocationUnder.push(toRow(budget, { ...used, amount: residual, rate: allocationRate }));

    const overBudget = overRow(budget, budget.allocated, used, utilisationRate);
    if (overBudget) utilisationOver.push(overBudget.row);
    const leftToBook = withinLimit(budget.allocated, used).unused;
    if (leftToBook > 0) utilisationUnder.push(toRow(budget, { ...used, amount: leftToBook, rate: utilisationRate }));

    if (!budget.can_have_case_management) return;
    const careUsed = { booked: budget.cm_booked, claimed: budget.cm_claimed };
    const usage = rateOf(budget.cm_booked + budget.cm_claimed, budget.cm_allowance);
    const overAllowance = overRow(budget, budget.cm_allowance, careUsed, usage);
    if (overAllowance) careOver.push(overAllowance.row);
    const { unused } = withinLimit(budget.cm_allowance, careUsed);
    if (budget.cm_allowance > 0 && unused > 0) {
      careUnder.push(toRow(budget, { ...careUsed, amount: unused, rate: usage }));
    }
  });

  const overTotals = (rows: BudgetListRow[]): OverTotals =>
    addOverLimits(
      rows.map(({ booked_over: booked, claimed_over: claimed }) => ({
        booked,
        claimed,
        total: (toCents(booked) + toCents(claimed)) / 100,
      })),
    );

  return {
    allocation: { over: toList(allocationOver), under: toList(allocationUnder) },
    utilisation: { over: toList(utilisationOver, overTotals(utilisationOver)), under: toList(utilisationUnder) },
    'care-management': { over: toList(careOver, overTotals(careOver)), under: toList(careUnder) },
  };
};

export interface DashboardBannerItem {
  key: BreakdownKey;
  tone: OverTone;
  text: string;
}

export interface DashboardBannerView {
  tone: OverTone;
  title: string;
  items: DashboardBannerItem[];
}

/**
 * One banner for everything over a limit this quarter, in the colour of the most serious line: Over-allocated (over
 * the funding: red), Over-utilised (over the agreed budgets) and Care management over allowance (amber).
 * Null when nothing is over.
 */
export const buildDashboardBanner = (breakdowns: DashboardBreakdowns): DashboardBannerView | null => {
  const copy = DASHBOARD_TEXT.banner;
  const items: DashboardBannerItem[] = [];

  const allocation = breakdowns.allocation.over;
  if (allocation.rows.length > 0) {
    items.push({
      key: 'allocation',
      tone: 'danger',
      text: copy.overAllocated(formatOverAmount(allocation.total), allocation.rows.length),
    });
  }

  const utilisation = breakdowns.utilisation.over;
  if (utilisation.over && utilisation.rows.length > 0) {
    items.push({
      key: 'utilisation',
      tone: 'warning',
      text: copy.overUtilised(formatOverAmount(utilisation.total), utilisation.rows.length),
    });
  }

  const careManagement = breakdowns['care-management'].over;
  if (careManagement.over && careManagement.rows.length > 0) {
    items.push({
      key: 'care-management',
      tone: 'warning',
      text: copy.careManagement(formatOverAmount(careManagement.total), careManagement.rows.length),
    });
  }

  const tone = mostSeriousTone(items.map((item) => item.tone));
  return tone ? { tone, title: copy.title(items.length), items } : null;
};
